import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import InlineArtifact from "../InlineArtifact";
import InlineApproval from "../InlineApproval";
import ArtifactSlideOver from "../ArtifactSlideOver";
import ConversationalOnboarding from "../ConversationalOnboarding";
import type { InlineArtifactData } from "../InlineArtifact";
import type { InlineApprovalData } from "../InlineApproval";
import { fetchAiSetup } from "../../connectAi";
import type { SSEEvent } from "../../hooks/useSSE";
import type { ChatMessage, ChatStreamItem, Attachment, InferenceStatus } from "./types";
import {
  approveApprovalItem,
  executeProposalItem,
  rejectApprovalItem,
  submitProposalFeedback,
  undoProposalItem,
} from "./api";
import { estimateTokens } from "./helpers";
import { loadMessages, saveMessages, saveFeedback, getFeedback } from "./persistence";
import { useSmartScroll } from "./useSmartScroll";
import AgentAvatar from "./AgentAvatar";
import UserMessage from "./UserMessage";
import AssistantMessage from "./AssistantMessage";
import SystemMessage from "./SystemMessage";
import EmptyState from "./EmptyState";
import InputBar from "./InputBar";
import StreamingIndicator from "./StreamingIndicator";
import ScrollToBottom from "./ScrollToBottom";
import FollowUpSuggestions from "./FollowUpSuggestions";
import KeyboardShortcuts from "./KeyboardShortcuts";
import ConversationSidebar from "./ConversationSidebar";
import UndoToast from "./UndoToast";
import { useLifeSelection } from "../life";
import {
  buildUnifiedChatRequest,
  resolveUnifiedChatRequestBillingSource,
} from "../../chat-runtime";
import type { WorkspaceTourSurface } from "../onboarding/registry";
import { resolveInlineArtifactOpenTarget } from "../workspaceAppHelpers";
import { buildBridgeContextSummary, buildBridgeMessagePrefix } from "./bridgeContext";
import {
  CHAT_BRIDGE_ACTIVE_KEY,
  CHAT_BRIDGE_EVENT,
  CHAT_BRIDGE_QUEUE_KEY,
  type BridgeMessage,
  type ChatBridgeContext,
} from "../chatBridge";
import { buildSelectedTargetProvenance, extractSelectedTargetFromContext } from "../selectedTarget";
import "./chat.css";

async function fetchInferenceStatus(): Promise<InferenceStatus> {
  const res = await fetch("/api/inference/status");
  if (!res.ok) {
    throw new Error("Failed to fetch inference status");
  }
  return res.json();
}

function parseInferenceStatusFromHeaders(response: Response): InferenceStatus | null {
  const mode = response.headers.get("x-agentme-inference-mode")?.trim();
  const provider = response.headers.get("x-agentme-inference-provider")?.trim();
  const model = response.headers.get("x-agentme-inference-model")?.trim();
  const qualityTier = response.headers.get("x-agentme-inference-quality-tier")?.trim();
  const reason = response.headers.get("x-agentme-inference-reason")?.trim();
  const isFallbackHeader = response.headers.get("x-agentme-inference-fallback")?.trim();

  if (!mode || !provider || !model || !qualityTier || !reason) return null;
  if (
    mode !== "managed" &&
    mode !== "subscription" &&
    mode !== "byo_key" &&
    mode !== "local_fallback"
  ) {
    return null;
  }
  if (qualityTier !== "enhanced" && qualityTier !== "standard" && qualityTier !== "degraded") {
    return null;
  }

  return {
    mode,
    provider,
    model,
    reason,
    qualityTier,
    isFallback: isFallbackHeader === "true",
  };
}

// ---- SSE event processors ----

function extractArtifactFromEvent(event: SSEEvent): InlineArtifactData | null {
  const d = event.data as Record<string, unknown>;
  if (!d || !d.artifactId) return null;
  const artifact = (d.artifact ?? d) as Record<string, unknown>;
  const blocks = (d.blocks ?? artifact.blocks ?? []) as Array<Record<string, unknown>>;
  return {
    artifactId: String(d.artifactId ?? artifact.id ?? `artifact-${event.timestamp}`),
    title: String(artifact.title ?? d.title ?? "Untitled Artifact"),
    kind: String(artifact.kind ?? d.kind ?? "document"),
    status: (artifact.status ?? d.status ?? "draft") as InlineArtifactData["status"],
    blocks: blocks.map((b, i) => ({
      blockId: String(b.blockId ?? `blk-${i}`),
      type: String(b.type ?? "markdown"),
      version: Number(b.version ?? 1),
      payload: (b.payload ?? b) as Record<string, unknown>,
      renderHints: b.renderHints as Record<string, string> | undefined,
      provenance: b.provenance as
        | { toolName?: string; runId?: string; sourceUrl?: string }
        | undefined,
      actions: b.actions as
        | Array<{ actionId: string; label: string; type: string; confirmRequired: boolean }>
        | undefined,
    })),
    createdAt: String(d.createdAt ?? artifact.createdAt ?? new Date(event.timestamp).toISOString()),
  };
}

function extractApprovalFromEvent(event: SSEEvent): InlineApprovalData | null {
  const d = event.data as Record<string, unknown>;
  if (!d || !d.id) return null;
  return {
    id: String(d.id),
    type: (d.type ?? "tool_usage") as InlineApprovalData["type"],
    status: (d.status ?? "pending") as InlineApprovalData["status"],
    priority: (d.priority ?? "normal") as InlineApprovalData["priority"],
    title: String(d.title ?? "Approval Request"),
    description: String(d.description ?? ""),
    requestedBy: String(d.requestedBy ?? "agent"),
    requestedAt: String(d.requestedAt ?? new Date(event.timestamp).toISOString()),
    expiresAt: d.expiresAt ? String(d.expiresAt) : undefined,
    payload: (d.payload ?? {}) as Record<string, unknown>,
  };
}

function buildImmediateStatusHint(text: string): string {
  const normalized = text.toLowerCase();
  let score = 1 + Math.min(5, Math.floor(text.length / 160));
  const weightedSignals: Array<[RegExp, number]> = [
    [/\b(find|research|analy[sz]e|investigate|map)\b/g, 2],
    [/\b(project|workstream|issue|organize|plan)\b/g, 1],
    [/\b(compare|rank|validate|verify|confidence)\b/g, 1],
    [/\b(connect|integration|api|subscription)\b/g, 1],
  ];
  for (const [pattern, weight] of weightedSignals) {
    if (pattern.test(normalized)) score += weight;
  }

  if (score >= 9) return "On it. I will update you tomorrow if this stays long-running.";
  if (score >= 6)
    return "On it. I will update you in about 30 minutes if this needs multiple passes.";
  if (score >= 4)
    return "On it. I will update you in about 5 minutes if this takes longer than expected.";
  return "On it. I will keep you updated as I work.";
}

const DEFAULT_REQUEST_MODEL = "auto" as const;

// ---- Props ----

interface ChatSpineProps {
  sessionId: string;
  sseEvents?: SSEEvent[];
  onSessionCostUpdate?: (cost: number, tokens: number) => void;
  needsOnboarding?: boolean;
  onboardingBlocker?: { reason: string; message: string } | null;
  onOnboardingComplete?: () => void;
  onAgentNameChange?: (name: string) => void;
  agentName?: string;
  onNewChat?: () => void;
  onSwitchConversation?: (sessionId: string) => void;
  onOpenSettings?: (target?: { sectionId?: string; pageId?: string }) => void;
  onOpenWorkspaceSurface?: (surface: WorkspaceTourSurface | "settings") => void;
  onOpenWorkspaceApp?: (appId: string, fallbackTitle?: string) => void;
}

// ---- Component ----

export default function ChatSpine({
  sessionId,
  sseEvents,
  onSessionCostUpdate,
  needsOnboarding = false,
  onboardingBlocker = null,
  onOnboardingComplete,
  onAgentNameChange,
  agentName,
  onNewChat,
  onSwitchConversation,
  onOpenSettings,
  onOpenWorkspaceSurface,
  onOpenWorkspaceApp,
}: ChatSpineProps): React.ReactElement {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessages(sessionId));
  const [inlineArtifacts, setInlineArtifacts] = useState<InlineArtifactData[]>([]);
  const [inlineApprovals, setInlineApprovals] = useState<InlineApprovalData[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState("");
  const [streamedTokens, setStreamedTokens] = useState(0);
  const [slideOverArtifact, setSlideOverArtifact] = useState<InlineArtifactData | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [liveInferenceStatus, setLiveInferenceStatus] = useState<InferenceStatus | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const processedEventIds = useRef<Set<string>>(new Set());
  const taskPollersRef = useRef<Set<string>>(new Set());
  const { selections, clearSelections } = useLifeSelection();

  const {
    containerRef,
    isAtBottom,
    newMessageCount,
    scrollToBottom,
    forceScrollToBottom,
    autoScroll,
  } = useSmartScroll();

  const inferenceStatusQ = useQuery({
    queryKey: ["inference-status"],
    queryFn: fetchInferenceStatus,
  });
  const aiSetupQ = useQuery({
    queryKey: ["connect-ai"],
    queryFn: fetchAiSetup,
  });
  const inferenceStatus = liveInferenceStatus ?? inferenceStatusQ.data ?? null;
  const requestBillingSource = resolveUnifiedChatRequestBillingSource({
    preferredSource: aiSetupQ.data?.preferredSource,
    connectedApiProviders: aiSetupQ.data?.connectedApiProviders,
    mode: inferenceStatus?.mode,
    routeTruth: inferenceStatusQ.data?.routeTruth,
  });

  const getModelCosts = useCallback(
    (_modelId: string) => ({
      inputCost: 0.000003,
      outputCost: 0.000015,
    }),
    [],
  );

  // Reload messages when sessionId changes
  useEffect(() => {
    setMessages(loadMessages(sessionId));
    setInlineArtifacts([]);
    setInlineApprovals([]);
    setStreamedContent("");
    setStreaming(false);
    processedEventIds.current.clear();
  }, [sessionId]);

  // Process SSE events
  useEffect(() => {
    if (!sseEvents) return;
    for (const event of sseEvents) {
      const eventKey = `${event.event}-${event.timestamp}`;
      if (processedEventIds.current.has(eventKey)) continue;
      processedEventIds.current.add(eventKey);

      if (event.event === "canvas:artifact_created" || event.event === "canvas:artifact_updated") {
        const artifact = extractArtifactFromEvent(event);
        if (artifact) {
          setInlineArtifacts((prev) => {
            const idx = prev.findIndex((a) => a.artifactId === artifact.artifactId);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = artifact;
              return updated;
            }
            return [...prev, artifact];
          });
        }
      }
      if (event.event === "approval:new" || event.event === "approval:updated") {
        const approval = extractApprovalFromEvent(event);
        if (approval) {
          setInlineApprovals((prev) => {
            const idx = prev.findIndex((a) => a.id === approval.id);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = approval;
              return updated;
            }
            return [...prev, approval];
          });
        }
      }
    }
  }, [sseEvents]);

  // Build sorted stream
  const streamItems = useMemo((): ChatStreamItem[] => {
    const items: ChatStreamItem[] = [];
    for (const msg of messages) items.push({ type: "message", data: msg });
    for (const art of inlineArtifacts) items.push({ type: "artifact", data: art });
    for (const apr of inlineApprovals) items.push({ type: "approval", data: apr });
    items.sort((a, b) => {
      const tsA =
        a.type === "message"
          ? a.data.timestamp
          : a.type === "artifact"
            ? new Date(a.data.createdAt).getTime()
            : new Date(a.data.requestedAt).getTime();
      const tsB =
        b.type === "message"
          ? b.data.timestamp
          : b.type === "artifact"
            ? new Date(b.data.createdAt).getTime()
            : new Date(b.data.requestedAt).getTime();
      return tsA - tsB;
    });
    return items;
  }, [messages, inlineArtifacts, inlineApprovals]);

  const hasContent = streamItems.length > 0;

  // Persist messages and update session cost
  useEffect(() => {
    saveMessages(sessionId, messages);
    const totalTokens = messages.reduce((sum, m) => sum + (m.tokenCount ?? 0), 0);
    const totalCost = messages.reduce((sum, m) => sum + (m.estimatedCost ?? 0), 0);
    onSessionCostUpdate?.(totalCost, totalTokens);
  }, [messages, sessionId, onSessionCostUpdate]);

  // Auto-scroll on new content
  useEffect(() => {
    autoScroll();
  }, [streamItems, streamedContent, autoScroll]);

  // Session totals
  const sessionTotalTokens = messages.reduce((sum, m) => sum + (m.tokenCount ?? 0), 0);
  const sessionTotalCost = messages.reduce((sum, m) => sum + (m.estimatedCost ?? 0), 0);

  // Check if last message is from assistant (for follow-up suggestions)
  const lastStreamItem = streamItems[streamItems.length - 1];
  const showFollowUp =
    !streaming && lastStreamItem?.type === "message" && lastStreamItem.data.role === "assistant";

  // ---- Handlers ----

  const pollTaskStatus = useCallback(
    (taskId: string) => {
      const cleanTaskId = taskId.trim();
      if (!cleanTaskId || taskPollersRef.current.has(cleanTaskId)) return;
      taskPollersRef.current.add(cleanTaskId);

      void (async () => {
        try {
          for (let attempt = 0; attempt < 120; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 2500));
            const res = await fetch(`/api/tasks/${encodeURIComponent(cleanTaskId)}`);
            if (!res.ok) continue;
            const json = (await res.json()) as {
              task?: {
                status?: string;
                output?: string;
                error?: string;
                executionModel?: string;
              };
            };
            const status = String(json.task?.status || "");
            if (status !== "completed" && status !== "failed") continue;

            const content =
              status === "completed"
                ? String(json.task?.output || "").trim() || "Task completed."
                : `Task failed: ${String(json.task?.error || "unknown error")}`;
            const completionModel = String(json.task?.executionModel || "auto");
            const completionTokens = estimateTokens(content);
            const costs = getModelCosts(completionModel);
            setMessages((prev) => [
              ...prev,
              {
                id: `msg-${Date.now()}-${cleanTaskId}`,
                role: "assistant",
                content,
                timestamp: Date.now(),
                tokenCount: completionTokens,
                estimatedCost: completionTokens * costs.outputCost,
                isNew: true,
              },
            ]);
            return;
          }
        } catch {
          // Swallow polling errors to avoid disrupting active chat sessions.
        } finally {
          taskPollersRef.current.delete(cleanTaskId);
        }
      })();
    },
    [getModelCosts],
  );

  // Core send logic — accepts optional overrides for re-send flows (edit, regenerate, retry).
  // When called with overrides, it skips creating a new user message (the caller already set up messages).
  const sendCore = useCallback(
    async (
      textToSend: string,
      historyOverride?: ChatMessage[],
      contextSummary?: string,
      bridgeContext?: ChatBridgeContext,
    ) => {
      if (!textToSend.trim() || streaming) return;

      const trimmed = textToSend.trim();
      const inputTokens = estimateTokens(trimmed);
      const costs = getModelCosts(DEFAULT_REQUEST_MODEL);

      // If no history override, this is a normal send — create user message
      const isResend = !!historyOverride;
      let apiMessages: ChatMessage[];

      if (!isResend) {
        // Inject life-selection context if items are selected on the Life canvas
        let contentForApi = trimmed;
        if (selections.length > 0) {
          const selectionXml = selections
            .map((s) => `  <item id="${s.itemId}" type="${s.primitiveType}" label="${s.label}" />`)
            .join("\n");
          contentForApi = `<life-selection>\n${selectionXml}\n</life-selection>\n${trimmed}`;
        }

        const userMsg: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: "user",
          content: trimmed, // Display the clean version
          timestamp: Date.now(),
          tokenCount: inputTokens,
          estimatedCost: inputTokens * costs.inputCost,
          isNew: true,
        };
        setMessages((prev) => [...prev, userMsg]);
        // API gets the version with life-selection context
        const apiUserMsg: ChatMessage = { ...userMsg, content: contentForApi };
        apiMessages = [...messages, apiUserMsg];
        clearSelections();
      } else {
        apiMessages = historyOverride;
      }

      setInput("");
      setAttachments([]);
      setStreaming(true);
      setStreamedContent("");
      setStreamedTokens(0);
      forceScrollToBottom();
      const requestId = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const statusMessageId = `sys-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: statusMessageId,
          role: "system",
          content: buildImmediateStatusHint(trimmed),
          timestamp: Date.now(),
          tokenCount: 0,
          estimatedCost: 0,
          transient: true,
        },
      ]);

      const controller = new AbortController();
      abortRef.current = controller;
      const normalizedSelectedTarget = extractSelectedTargetFromContext(bridgeContext);
      const { selectedTarget: _ignoredSelectedTarget, ...bridgeContextWithoutSelectedTarget } =
        (bridgeContext ?? {}) as Record<string, unknown>;
      const normalizedBridgeContext = bridgeContext
        ? {
            ...bridgeContextWithoutSelectedTarget,
            ...(normalizedSelectedTarget ? { selectedTarget: normalizedSelectedTarget } : {}),
          }
        : bridgeContext;

      try {
        const response = await fetch("/api/chat/completions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            buildUnifiedChatRequest({
              model: DEFAULT_REQUEST_MODEL,
              sessionId,
              billingSource: requestBillingSource,
              stream: true,
              surface: "workspace_chat",
              requestId,
              selections,
              contextSummary,
              uiContext: normalizedBridgeContext,
              provenance: buildSelectedTargetProvenance(normalizedSelectedTarget),
              messages: apiMessages.map((m) => ({ role: m.role, content: m.content })),
            }),
          ),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Chat request failed: ${response.status}`);
        const responseInferenceStatus = parseInferenceStatusFromHeaders(response);
        if (responseInferenceStatus) {
          setLiveInferenceStatus(responseInferenceStatus);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullContent = "";
        let queuedTaskId: string | null = null;

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            for (const line of chunk.split("\n")) {
              if (line.startsWith("data: ")) {
                const data = line.slice(6);
                if (data === "[DONE]") break;
                try {
                  const parsed = JSON.parse(data);
                  if (parsed?._workflow?.state === "running") {
                    // Runtime status chunks are rendered separately as transient system hints.
                    continue;
                  }
                  const parsedTaskId =
                    typeof parsed?._workflow?.taskId === "string"
                      ? parsed._workflow.taskId.trim()
                      : "";
                  if (parsedTaskId) {
                    queuedTaskId = parsedTaskId;
                  }
                  const token =
                    parsed.choices?.[0]?.delta?.content || parsed.content || parsed.text || "";
                  fullContent += token;
                  setStreamedContent(fullContent);
                  setStreamedTokens(estimateTokens(fullContent));
                } catch {
                  fullContent += data;
                  setStreamedContent(fullContent);
                  setStreamedTokens(estimateTokens(fullContent));
                }
              }
            }
          }
        } else {
          const json = await response.json();
          fullContent =
            json.choices?.[0]?.message?.content ||
            json.content ||
            json.message ||
            "No response received.";
        }

        const outputTokens = estimateTokens(fullContent);
        setMessages((prev) => [
          ...prev.filter((m) => !m.transient),
          {
            id: `msg-${Date.now()}`,
            role: "assistant",
            content: fullContent || "(empty response)",
            timestamp: Date.now(),
            tokenCount: outputTokens,
            estimatedCost: outputTokens * costs.outputCost,
            isNew: true,
          },
        ]);
        if (queuedTaskId) {
          pollTaskStatus(queuedTaskId);
        }
      } catch (err: unknown) {
        if ((err as Error).name === "AbortError") {
          if (streamedContent) {
            const cancelledTokens = estimateTokens(streamedContent);
            setMessages((prev) => [
              ...prev.filter((m) => !m.transient),
              {
                id: `msg-${Date.now()}`,
                role: "assistant",
                content: streamedContent + "\n\n[Cancelled]",
                timestamp: Date.now(),
                tokenCount: cancelledTokens,
                estimatedCost: cancelledTokens * costs.outputCost,
                isNew: true,
              },
            ]);
          }
        } else {
          setMessages((prev) => [
            ...prev.filter((m) => !m.transient),
            {
              id: `msg-${Date.now()}`,
              role: "system",
              content: `Error: ${(err as Error).message}`,
              timestamp: Date.now(),
              tokenCount: 0,
              estimatedCost: 0,
            },
          ]);
        }
      } finally {
        setMessages((prev) => prev.filter((m) => !m.transient));
        setStreaming(false);
        setStreamedContent("");
        setStreamedTokens(0);
        abortRef.current = null;
      }
    },
    [
      streaming,
      sessionId,
      messages,
      streamedContent,
      getModelCosts,
      forceScrollToBottom,
      pollTaskStatus,
      selections,
      clearSelections,
    ],
  );

  // Listen for workspace chat bridge events from anywhere in the shell.
  useEffect(() => {
    const bridgeWindow = window as Window & {
      [CHAT_BRIDGE_ACTIVE_KEY]?: boolean;
      [CHAT_BRIDGE_QUEUE_KEY]?: BridgeMessage[];
    };
    const removeQueuedMessage = (messageId?: string) => {
      if (!messageId) return;
      const queue = bridgeWindow[CHAT_BRIDGE_QUEUE_KEY];
      if (!Array.isArray(queue) || queue.length === 0) return;
      bridgeWindow[CHAT_BRIDGE_QUEUE_KEY] = queue.filter((m) => m.id !== messageId);
    };

    const submitBridgeMessage = (detail: BridgeMessage) => {
      if (!detail?.text) return;
      const prefix = buildBridgeMessagePrefix(detail.context);
      const contextSummary = buildBridgeContextSummary(detail.context);
      sendCore(prefix + detail.text, undefined, contextSummary, detail.context);
    };

    bridgeWindow[CHAT_BRIDGE_ACTIVE_KEY] = true;

    const queued = bridgeWindow[CHAT_BRIDGE_QUEUE_KEY];
    if (Array.isArray(queued) && queued.length > 0) {
      const first = queued[0];
      removeQueuedMessage(first?.id);
      if (first) {
        submitBridgeMessage(first);
      }
    }

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as BridgeMessage;
      removeQueuedMessage(detail?.id);
      submitBridgeMessage(detail);
    };

    window.addEventListener(CHAT_BRIDGE_EVENT, handler);
    return () => {
      bridgeWindow[CHAT_BRIDGE_ACTIVE_KEY] = false;
      window.removeEventListener(CHAT_BRIDGE_EVENT, handler);
    };
  }, [sendCore]);

  const handleSend = useCallback(() => {
    sendCore(input);
  }, [input, sendCore]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleCopy = useCallback((_content: string) => {
    /* Toast handled in MessageActions */
  }, []);

  const handleRegenerate = useCallback(
    (messageId: string) => {
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      // Remove the assistant message and everything after it
      const preceding = messages.slice(0, idx);
      // The preceding history already includes the user message — just re-send
      setMessages(preceding);
      sendCore(preceding[preceding.length - 1]?.content ?? "", preceding);
    },
    [messages, sendCore],
  );

  const handleEdit = useCallback(
    (messageId: string, newContent: string) => {
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      // Replace message content and remove everything after it
      const updated = messages.slice(0, idx);
      const editedMsg: ChatMessage = { ...messages[idx], content: newContent };
      const newHistory = [...updated, editedMsg];
      setMessages(newHistory);
      sendCore(newContent, newHistory);
    },
    [messages, sendCore],
  );

  const handleFeedback = useCallback((messageId: string, feedback: "positive" | "negative") => {
    saveFeedback(messageId, feedback);
    const current = getFeedback(messageId);
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, feedback: current ?? undefined } : m)),
    );
  }, []);

  const handleRetry = useCallback(() => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    // Remove the error message(s) at the end
    const cleaned = messages.filter(
      (m) => !(m.role === "system" && m.timestamp === messages[messages.length - 1]?.timestamp),
    );
    setMessages(cleaned);
    sendCore(lastUser.content, cleaned);
  }, [messages, sendCore]);

  const handleOpenArtifact = useCallback(
    (artifactId: string) => {
      const found = inlineArtifacts.find((a) => a.artifactId === artifactId);
      if (!found) return;
      const target = resolveInlineArtifactOpenTarget(found);
      if (target.kind === "workspace_app" && onOpenWorkspaceApp) {
        onOpenWorkspaceApp(target.appId, target.title);
        return;
      }
      setSlideOverArtifact(found);
    },
    [inlineArtifacts, onOpenWorkspaceApp],
  );

  const handleApproveItem = useCallback(
    async (id: string, notes?: string, edits?: unknown) => {
      const approval = inlineApprovals.find((a) => a.id === id);
      const type = approval?.type;
      await approveApprovalItem(id, notes, type, edits);
      setInlineApprovals((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: "approved" as const } : a)),
      );
      if (type === "proposal_card") {
        try {
          await executeProposalItem(id);
        } catch {
          /* SSE will refresh */
        }
      }
    },
    [inlineApprovals],
  );

  const handleRejectItem = useCallback(
    async (id: string, notes?: string) => {
      const approval = inlineApprovals.find((a) => a.id === id);
      const type = approval?.type;
      await rejectApprovalItem(id, notes, type);
      setInlineApprovals((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: "rejected" as const } : a)),
      );
    },
    [inlineApprovals],
  );

  const handleUndoItem = useCallback(async (id: string) => {
    try {
      await undoProposalItem(id);
    } catch {
      /* SSE will refresh */
    }
  }, []);

  const handleRetryItem = useCallback(async (id: string) => {
    try {
      await executeProposalItem(id);
    } catch {
      /* SSE will refresh */
    }
  }, []);

  const handleFeedbackItem = useCallback(
    async (id: string, rating: "positive" | "negative", tags?: string[], comment?: string) => {
      try {
        await submitProposalFeedback(id, rating, tags, comment);
      } catch {
        /* Optimistic UI handles success; SSE will reconcile if needed. */
      }
    },
    [],
  );

  const handleCopyLastResponse = useCallback(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    if (lastAssistant) {
      navigator.clipboard.writeText(lastAssistant.content);
    }
  }, [messages]);

  const handleFollowUp = useCallback(
    (text: string) => {
      forceScrollToBottom();
      sendCore(text);
    },
    [forceScrollToBottom, sendCore],
  );

  return (
    <div className="flex flex-col h-full relative">
      {/* Keyboard shortcuts handler */}
      <KeyboardShortcuts onCopyLastResponse={handleCopyLastResponse} onNewChat={onNewChat} />

      {/* Messages area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        {onboardingBlocker && (
          <div className="max-w-3xl mx-auto mb-4">
            <div
              className="rounded-2xl px-4 py-3"
              style={{
                border: "1px solid var(--c-warning)",
                backgroundColor: "rgba(234, 179, 8, 0.08)",
              }}
            >
              <div
                className="text-[11px] font-semibold uppercase mb-1"
                style={{ color: "var(--c-warning)" }}
              >
                Setup needs attention
              </div>
              <p className="text-sm" style={{ color: "var(--c-text)" }}>
                {onboardingBlocker.message}
              </p>
              <p className="text-[11px] mt-1" style={{ color: "var(--c-text-faint)" }}>
                Reason: {onboardingBlocker.reason}
              </p>
            </div>
          </div>
        )}

        {/* Onboarding or empty state */}
        {!hasContent && !streaming && needsOnboarding && onOnboardingComplete && (
          <div className="flex items-start justify-center pt-8 h-full overflow-y-auto">
            <ConversationalOnboarding
              onComplete={onOnboardingComplete}
              onAgentNameChange={onAgentNameChange}
              initialAgentName={agentName}
              onOpenSettings={onOpenSettings}
              onOpenWorkspaceSurface={onOpenWorkspaceSurface}
              blocker={onboardingBlocker}
            />
          </div>
        )}
        {!hasContent && !streaming && !needsOnboarding && (
          <EmptyState
            agentName={agentName}
            onAction={(text) => sendCore(text)}
            onPrefill={(text) => setInput(text)}
          />
        )}

        {/* Stream items */}
        <div className="max-w-3xl mx-auto space-y-4">
          {streamItems.map((item, idx) => {
            if (item.type === "message") {
              const prevItem = idx > 0 ? streamItems[idx - 1] : null;
              const isFirstInGroup =
                !prevItem || prevItem.type !== "message" || prevItem.data.role !== item.data.role;

              if (item.data.role === "user") {
                return (
                  <UserMessage
                    key={item.data.id}
                    message={item.data}
                    onEdit={handleEdit}
                    onCopy={handleCopy}
                  />
                );
              }
              if (item.data.role === "system") {
                return (
                  <SystemMessage key={item.data.id} message={item.data} onRetry={handleRetry} />
                );
              }
              return (
                <AssistantMessage
                  key={item.data.id}
                  message={item.data}
                  isFirstInGroup={isFirstInGroup}
                  onCopy={handleCopy}
                  onRegenerate={handleRegenerate}
                  onFeedback={handleFeedback}
                  currentFeedback={getFeedback(item.data.id)}
                />
              );
            }
            if (item.type === "artifact") {
              return (
                <div key={`artifact-${item.data.artifactId}`} className="flex gap-3">
                  <AgentAvatar className="mt-3" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
                        created an artifact
                      </span>
                    </div>
                    <InlineArtifact artifact={item.data} onOpenFull={handleOpenArtifact} />
                  </div>
                </div>
              );
            }
            if (item.type === "approval") {
              return (
                <div key={`approval-${item.data.id}`} className="flex gap-3">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 mt-3"
                    style={{
                      backgroundColor: "rgba(234, 179, 8, 0.15)",
                      color: "var(--c-warning)",
                    }}
                  >
                    !
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-[10px] font-semibold uppercase"
                        style={{ color: "var(--c-warning)" }}
                      >
                        Approval Needed
                      </span>
                    </div>
                    <InlineApproval
                      approval={item.data}
                      onApprove={handleApproveItem}
                      onReject={handleRejectItem}
                      onUndo={handleUndoItem}
                      onRetry={handleRetryItem}
                      onFeedback={handleFeedbackItem}
                    />
                  </div>
                </div>
              );
            }
            return null;
          })}

          {/* Follow-up suggestions */}
          {showFollowUp && <FollowUpSuggestions visible onSelect={handleFollowUp} />}

          {/* Streaming indicator */}
          {streaming && <StreamingIndicator content={streamedContent} tokens={streamedTokens} />}
        </div>
      </div>

      {/* Scroll-to-bottom button */}
      <ScrollToBottom
        visible={!isAtBottom}
        newMessageCount={newMessageCount}
        onClick={scrollToBottom}
      />

      {/* Conversation history quick access */}
      {onSwitchConversation && onNewChat && (
        <div className="px-4 md:px-6 pb-2">
          <div className="max-w-3xl mx-auto flex justify-end">
            <ConversationSidebar
              currentSessionId={sessionId}
              onSelectConversation={onSwitchConversation}
              onNewChat={onNewChat}
              trigger={
                <button
                  className="px-3 py-1.5 rounded-md text-xs transition-colors"
                  style={{
                    border: "1px solid var(--c-border)",
                    backgroundColor: "var(--c-surface)",
                    color: "var(--c-text-secondary)",
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = "var(--c-hover)";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = "var(--c-surface)";
                  }}
                >
                  Conversation history
                </button>
              }
            />
          </div>
        </div>
      )}

      {/* Phase 4.2.d — undo toast strip. Polls /api/undo every 5s; renders one
          toast per active descriptor. Positioned above the composer so it
          doesn't obscure the input but stays close enough to attract attention. */}
      <UndoToast />

      {/* Keep the main composer visually separate from the shell status strip. */}
      <div className="pb-4 md:pb-6">
        <InputBar
          input={input}
          onInputChange={setInput}
          onSend={handleSend}
          onStop={handleStop}
          streaming={streaming}
          model={DEFAULT_REQUEST_MODEL}
          onModelChange={() => {}}
          availableModels={[]}
          sessionTotalTokens={sessionTotalTokens}
          sessionTotalCost={sessionTotalCost}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          inferenceStatus={inferenceStatus}
          showKeyboardHint={false}
        />
      </div>

      {/* Artifact slide-over */}
      <ArtifactSlideOver
        artifact={slideOverArtifact}
        open={slideOverArtifact !== null}
        onClose={() => setSlideOverArtifact(null)}
      />
    </div>
  );
}
